import { session, type Session } from './session';
import type { Staff, TokenPair } from './types';

export const API_URL = (import.meta.env.VITE_API_URL || 'http://localhost:4000').replace(/\/$/, '');

/** An error from the server, in its one shape: `{ statusCode, code, message, requestId }`. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly requestId?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export const isApiError = (e: unknown, code?: string): e is ApiError =>
  e instanceof ApiError && (code === undefined || e.code === code);

/** New key per money action (payout, adjustment); the same key is reused if that action is retried. */
export const newIdempotencyKey = () =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `k${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;

const MONEY_ERRORS: Record<string, string> = {
  NOTHING_TO_PAY_OUT: 'Nothing to pay: the driver isn’t owed anything, or it’s all in payouts already waiting.',
  PAYOUT_TOO_MUCH: 'That’s more than the driver can be paid now.',
  REFERENCE_REQUIRED: 'Add the UTR / reference of the transfer first.',
  PAYOUT_ALREADY_PAID: 'This payout is already marked paid. Correct it with an adjustment.',
  PAYOUT_CANCELLED: 'This payout was cancelled.',
  PAYOUT_CHANGED: 'Someone else just changed this payout. Look again.',
  IDEMPOTENCY_KEY_REUSED: 'This was already sent with different numbers. Close and start again.',
  IDEMPOTENCY_KEY_REQUIRED: 'Close and start again.',
  NOTHING_TO_SETTLE: 'Nothing to settle that way.',
  SETTLE_TOO_MUCH: 'That’s more than is owed.',
};

/** Plain message for any error, safe to show. */
export function errorMessage(e: unknown): string {
  if (e instanceof ApiError && MONEY_ERRORS[e.code]) return MONEY_ERRORS[e.code];
  if (e instanceof ApiError) {
    if (e.status === 403 && e.code === 'ROLE_REQUIRED') return 'Only the owner can do this.';
    if (e.status === 0) return e.message;
    return e.message || 'Something went wrong. Please try again.';
  }
  return 'Something went wrong. Please try again.';
}

type Query = Record<string, string | number | boolean | undefined | null>;

function url(path: string, query?: Query) {
  const u = new URL(`${API_URL}/v1${path}`);
  if (query)
    for (const [k, v] of Object.entries(query)) if (v !== undefined && v !== null && v !== '') u.searchParams.set(k, String(v));
  return u.toString();
}

async function send(method: string, path: string, body: unknown, token: string | undefined, query?: Query, extra?: Record<string, string>) {
  let res: Response;
  try {
    res = await fetch(url(path, query), {
      method,
      headers: {
        ...(body !== undefined ? { 'content-type': 'application/json' } : {}),
        ...(token ? { authorization: `Bearer ${token}` } : {}),
        ...extra,
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch {
    throw new ApiError(0, 'NETWORK', 'Cannot reach the Zoomo Rides server. Check the connection.');
  }
  return res;
}

async function parse<T>(res: Response): Promise<T> {
  const text = await res.text();
  const data = text ? JSON.parse(text) : null;
  if (!res.ok) {
    throw new ApiError(
      res.status,
      data?.code ?? 'ERROR',
      data?.message ?? `Request failed (${res.status})`,
      data?.requestId ?? res.headers.get('x-request-id') ?? undefined,
    );
  }
  return data as T;
}

// ───── refresh: one at a time, across tabs ─────

let inflight: Promise<boolean> | null = null;

async function doRefresh(failedAccessToken: string | undefined): Promise<boolean> {
  // Another tab may already have rotated the tokens: use theirs instead of replaying an old refresh token
  // (the server treats a replayed refresh token as theft and signs the account out everywhere).
  const latest = session.reload();
  if (!latest) return false;
  if (failedAccessToken && latest.accessToken !== failedAccessToken) return true;
  const res = await send('POST', '/auth/refresh', { refreshToken: latest.refreshToken }, undefined);
  if (!res.ok) {
    if (res.status === 401 || res.status === 403) session.set(null);
    return false;
  }
  const pair = (await res.json()) as TokenPair;
  session.set({ ...latest, accessToken: pair.accessToken, refreshToken: pair.refreshToken });
  return true;
}

/** Refresh the access token. Resolves true when a fresh token is in the session. */
export function refreshSession(failedAccessToken?: string): Promise<boolean> {
  if (!inflight) {
    const run = () => doRefresh(failedAccessToken);
    const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
    const p: Promise<boolean> = locks ? locks.request('zr.admin.refresh', () => run()).then((v) => v) : run();
    inflight = p.finally(() => {
      inflight = null;
    });
    return inflight;
  }
  return inflight;
}

export async function request<T>(method: string, path: string, opts: { body?: unknown; query?: Query; headers?: Record<string, string> } = {}) {
  const token = session.get()?.accessToken;
  let res = await send(method, path, opts.body, token, opts.query, opts.headers);
  if (res.status === 401 && token) {
    if (await refreshSession(token)) res = await send(method, path, opts.body, session.get()?.accessToken, opts.query, opts.headers);
    else session.set(null);
  }
  return parse<T>(res);
}

export const api = {
  get: <T>(path: string, query?: Query) => request<T>('GET', path, { query }),
  post: <T>(path: string, body?: unknown, headers?: Record<string, string>) => request<T>('POST', path, { body: body ?? {}, headers }),
  put: <T>(path: string, body: unknown) => request<T>('PUT', path, { body }),
  patch: <T>(path: string, body: unknown) => request<T>('PATCH', path, { body }),
  del: <T>(path: string) => request<T>('DELETE', path),
};

// ───── sign in / out ─────

export async function login(email: string, password: string): Promise<Session> {
  const res = await send('POST', '/admin/auth/login', { email, password }, undefined);
  const data = await parse<TokenPair & { admin: Staff }>(res);
  const s: Session = { accessToken: data.accessToken, refreshToken: data.refreshToken, admin: data.admin };
  session.set(s);
  return s;
}

export async function logout(): Promise<void> {
  const s = session.get();
  session.set(null);
  if (s) await send('POST', '/auth/logout', { refreshToken: s.refreshToken }, undefined).catch(() => undefined);
}

/** Check a stored session on start-up (refreshing if needed) and pick up role changes. */
export async function restoreSession(): Promise<Session | null> {
  const s = session.get();
  if (!s) return null;
  try {
    const admin = await api.get<Staff>('/admin/me');
    const now = session.get();
    if (!now) return null;
    session.set({ ...now, admin });
    return session.get();
  } catch (e) {
    if (e instanceof ApiError && (e.status === 401 || e.status === 403)) session.set(null);
    return session.get();
  }
}
